import React, { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { fetchPeriodSlugDetails } from '@/utils/tagsAPI';
import type { TagConfig, TagData, PeriodSlugDetails } from './types';

interface TagsRendererProps {
  config: TagConfig;
  data?: TagData;
  isLoading?: boolean;
  error?: Error | null;
}

export const TagsRenderer: React.FC<TagsRendererProps> = ({
  config,
  data,
  isLoading,
  error,
}) => {
  const [expandedPeriods, setExpandedPeriods] = useState<Set<string>>(new Set());
  const [periodDetails, setPeriodDetails] = useState<Map<string, PeriodSlugDetails>>(new Map());
  const [loadingPeriods, setLoadingPeriods] = useState<Set<string>>(new Set());
  const [periodFilters, setPeriodFilters] = useState<Map<string, 'all' | 'new' | 'lost'>>(new Map());

  // Fetch period details with current filter
  const fetchDetails = async (period: string, filter: 'all' | 'new' | 'lost' = 'all') => {
    if (!data) return;

    const sortedPeriods = data.periods.map(p => p.period);
    setLoadingPeriods(new Set(loadingPeriods).add(period));
    try {
      const details = await fetchPeriodSlugDetails(config.tagId, period, filter, sortedPeriods);
      setPeriodDetails(new Map(periodDetails).set(period, details));
    } catch (err) {
      console.error('Failed to load period details:', err);
    } finally {
      const newLoading = new Set(loadingPeriods);
      newLoading.delete(period);
      setLoadingPeriods(newLoading);
    }
  };

  // Handle row expansion
  const handleRowClick = async (period: string) => {
    const isExpanded = expandedPeriods.has(period);

    if (isExpanded) {
      // Collapse
      const newExpanded = new Set(expandedPeriods);
      newExpanded.delete(period);
      setExpandedPeriods(newExpanded);
    } else {
      // Expand
      const newExpanded = new Set(expandedPeriods);
      newExpanded.add(period);
      setExpandedPeriods(newExpanded);

      // Fetch details if not already loaded
      if (!periodDetails.has(period)) {
        const currentFilter = periodFilters.get(period) || 'all';
        await fetchDetails(period, currentFilter);
      }
    }
  };

  // Handle filter change
  const handleFilterChange = async (period: string, filter: 'all' | 'new' | 'lost') => {
    setPeriodFilters(new Map(periodFilters).set(period, filter));
    await fetchDetails(period, filter);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="w-full">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-[200px] bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full">
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-red-900">Failed to load tag data</h4>
            <p className="text-sm text-red-700 mt-1">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!data || !data.periods || data.periods.length === 0) {
    return (
      <div className="w-full text-center py-8 text-gray-500">
        <p>No data available for the selected periods</p>
      </div>
    );
  }

  // Use summary data for unique counts across all periods
  const totalDetected = data.summary.uniqueTagged;
  const totalNotDetected = data.summary.uniqueNotTagged;
  const totalUnknown = data.summary.uniqueUnknown;
  const grandTotal = data.summary.totalPages;

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Tag: {config.tagName}
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Showing {data.periods.length} period{data.periods.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Summary Cards - Unique pages across all selected periods */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-xs text-green-700 uppercase font-medium">Tagged</p>
          <p className="text-2xl font-bold text-green-900 mt-1">{totalDetected.toLocaleString()}</p>
          <p className="text-xs text-green-600 mt-1">
            {grandTotal > 0 ? ((totalDetected / grandTotal) * 100).toFixed(1) : '0'}% of unique pages
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-xs text-red-700 uppercase font-medium">No Tag</p>
          <p className="text-2xl font-bold text-red-900 mt-1">{totalNotDetected.toLocaleString()}</p>
          <p className="text-xs text-red-600 mt-1">
            {grandTotal > 0 ? ((totalNotDetected / grandTotal) * 100).toFixed(1) : '0'}% of unique pages
          </p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-700 uppercase font-medium">Unknown (Δ)</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totalUnknown.toLocaleString()}</p>
          <p className="text-xs text-gray-600 mt-1">
            {grandTotal > 0 ? ((totalUnknown / grandTotal) * 100).toFixed(1) : '0'}% of unique pages
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-xs text-blue-700 uppercase font-medium">Total Pages</p>
          <p className="text-2xl font-bold text-blue-900 mt-1">{grandTotal.toLocaleString()}</p>
          <p className="text-xs text-blue-600 mt-1">from pages/slugs API</p>
        </div>
      </div>

      {/* Data Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Period
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tagged
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                No Tag
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Unknown (Δ)
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lost Tag
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Gained Tag
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.periods.map((period) => {
              const isExpanded = expandedPeriods.has(period.period);
              const details = periodDetails.get(period.period);
              const isLoadingDetails = loadingPeriods.has(period.period);

              return (
                <React.Fragment key={period.period}>
                  <tr
                    onClick={() => handleRowClick(period.period)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                        {period.period}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                      <div className="flex flex-col items-end">
                        <span className="font-medium">{period.detected.toLocaleString()}</span>
                        <span className="text-xs text-green-600">
                          {period.total > 0 ? ((period.detected / period.total) * 100).toFixed(1) : '0'}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                      <div className="flex flex-col items-end">
                        <span className="font-medium">{period.not_detected.toLocaleString()}</span>
                        <span className="text-xs text-red-600">
                          {period.total > 0 ? ((period.not_detected / period.total) * 100).toFixed(1) : '0'}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                      <div className="flex flex-col items-end">
                        <span className="font-medium">{period.unknown.toLocaleString()}</span>
                        <span className="text-xs text-gray-600">
                          {period.total > 0 ? ((period.unknown / period.total) * 100).toFixed(1) : '0'}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                      <div className="flex flex-col items-end">
                        <span className="font-medium text-orange-900">{period.lostTag.toLocaleString()}</span>
                        <span className="text-xs text-orange-600">
                          -{period.lostTag > 0 ? ((period.lostTag / period.detected) * 100).toFixed(1) : '0'}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                      <div className="flex flex-col items-end">
                        <span className="font-medium text-blue-900">{period.gainedTag.toLocaleString()}</span>
                        <span className="text-xs text-blue-600">
                          +{period.gainedTag > 0 ? ((period.gainedTag / period.detected) * 100).toFixed(1) : '0'}%
                        </span>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Details Row */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={6} className="px-4 py-4 bg-gray-50">
                        {isLoadingDetails ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-pulse text-sm text-gray-500">Loading details...</div>
                          </div>
                        ) : details && details.slugs.length > 0 ? (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-semibold text-gray-900">
                                {periodFilters.get(period.period) === 'new'
                                  ? `Pages that gained "${config.tagName}" tag`
                                  : periodFilters.get(period.period) === 'lost'
                                  ? `Pages that lost "${config.tagName}" tag`
                                  : `Pages with "${config.tagName}" tag`} ({details.slugs.length})
                              </h4>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleFilterChange(period.period, 'all')}
                                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                    (periodFilters.get(period.period) || 'all') === 'all'
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  All
                                </button>
                                <button
                                  onClick={() => handleFilterChange(period.period, 'new')}
                                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                    periodFilters.get(period.period) === 'new'
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  New in Quarter
                                </button>
                                <button
                                  onClick={() => handleFilterChange(period.period, 'lost')}
                                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                    periodFilters.get(period.period) === 'lost'
                                      ? 'bg-orange-600 text-white'
                                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  Lost in Quarter
                                </button>
                              </div>
                            </div>
                            {periodFilters.get(period.period) === 'lost' && (
                              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                                <p className="text-sm text-orange-800">
                                  <span className="font-semibold">Note:</span> Showing data from the previous quarter.
                                  These pages had the "{config.tagName}" tag in the previous period but it was no longer observed in {period.period}.
                                </p>
                              </div>
                            )}
                            <div className="grid grid-cols-1 gap-3">
                              {details.slugs.map((slug) => (
                                <div
                                  key={slug.slug}
                                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                                >
                                  <div className="flex gap-4">
                                    {/* Left: Text Info */}
                                    <div className="flex-1 space-y-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-gray-900">{slug.slug}</span>
                                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                                          slug.confidence === 'high' ? 'bg-green-100 text-green-700' :
                                          slug.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                          'bg-gray-100 text-gray-700'
                                        }`}>
                                          {slug.confidence} confidence
                                        </span>
                                      </div>
                                      <div className="text-sm text-gray-600">
                                        <span className="font-medium">Reason:</span> {slug.reason}
                                      </div>
                                    </div>

                                    {/* Right: Annotated Image */}
                                    {slug.annotated_url && (
                                      <div className="w-48 flex-shrink-0">
                                        <p className="text-xs text-gray-500 mb-1">Annotated</p>
                                        <a
                                          href={slug.annotated_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="block relative group"
                                        >
                                          <img
                                            src={slug.annotated_url}
                                            alt={`${slug.slug} annotated`}
                                            className="w-full h-24 object-cover rounded border border-gray-200 hover:border-blue-500 transition-colors"
                                          />
                                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded flex items-center justify-center">
                                            <ExternalLink className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                          </div>
                                        </a>
                                        <a
                                          href={slug.screenshot_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1 inline-flex items-center gap-1"
                                        >
                                          <ExternalLink className="h-3 w-3" />
                                          Full Screenshot
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-semibold text-gray-900">
                                Pages with "{config.tagName}" tag (0)
                              </h4>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleFilterChange(period.period, 'all')}
                                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                    (periodFilters.get(period.period) || 'all') === 'all'
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  All
                                </button>
                                <button
                                  onClick={() => handleFilterChange(period.period, 'new')}
                                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                    periodFilters.get(period.period) === 'new'
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  New in Quarter
                                </button>
                                <button
                                  onClick={() => handleFilterChange(period.period, 'lost')}
                                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                    periodFilters.get(period.period) === 'lost'
                                      ? 'bg-orange-600 text-white'
                                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  Lost in Quarter
                                </button>
                              </div>
                            </div>
                            <div className="text-center py-8 text-gray-500">
                              {periodFilters.get(period.period) === 'new'
                                ? 'No new pages with this tag in this quarter'
                                : periodFilters.get(period.period) === 'lost'
                                ? 'No pages lost this tag in this quarter'
                                : 'No pages with tag detected for this period'}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
